// Share links: list, create, copy again, extend / visibility / revoke, and the shared report by token
// (architecture sprint-05 §6.4, §6.6, §6.7, §6.10; sprint-06 §6.5). The token goes in the POST body,
// never in a URL path.
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { LocalDate } from '@shared/calendar';
import type { TeamReportDto } from '@shared/team-report';
import { ApiClientError, apiRequest } from '@/lib/api';
import type { CreatedShareLink, CreateShareLinkInput, ShareLink } from '@/types/admin';

export const SHARE_LINKS_KEY = ['admin', 'share-links'] as const;
const HTTP_NOT_FOUND = 404;
const MAX_SHARED_RETRIES = 1;

export function useShareLinks(): UseQueryResult<ShareLink[]> {
  return useQuery({
    queryKey: SHARE_LINKS_KEY,
    queryFn: async () => (await apiRequest<{ items: ShareLink[] }>('/api/admin/share-links')).items,
  });
}

/** Errors (e.g. APP_URL_NOT_CONFIGURED) use the global toast. */
/** Sprint 17: the link stores the Team report selection (projects incl. 'none', members; empty = all). */
export function useCreateShareLink(): UseMutationResult<CreatedShareLink, Error, CreateShareLinkInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (range) => apiRequest<CreatedShareLink>('/api/admin/share-links', { method: 'POST', body: JSON.stringify(range) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SHARE_LINKS_KEY }),
  });
}

/** GET the URL of an active link. Silent: the page shows its own toasts (§6.7). */
export function useCopyShareLink(): UseMutationResult<{ url: string }, Error, string> {
  return useMutation({
    mutationFn: (id) => apiRequest<{ url: string }>(`/api/admin/share-links/${id}/url`),
    meta: { silent: true },
  });
}

// ── Sprint 6 (architecture sprint-06 §6.5) ──

/**
 * Signed-out viewers only. One request per page view (the public limiter counts each): no retry, no
 * refetch on focus, never cached. skipAuth: no token is sent and a 401 never redirects.
 */
export function usePublicSharedReport(token: string, { enabled }: { enabled: boolean }): UseQueryResult<{ report: TeamReportDto }> {
  return useQuery({
    queryKey: ['public-shared-report', token],
    queryFn: () =>
      apiRequest<{ report: TeamReportDto }>('/api/public/share/report', { method: 'POST', body: JSON.stringify({ token }), skipAuth: true }),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: 0,
    meta: { silent: true },
  });
}

/** Replaces the changed link in the list cache, then refetches the list. */
function useShareLinkMutation<V>(request: (vars: V) => Promise<{ link: ShareLink }>): UseMutationResult<{ link: ShareLink }, Error, V> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    meta: { silent: true },
    onSuccess: ({ link }) => {
      queryClient.setQueryData<ShareLink[]>(SHARE_LINKS_KEY, (items) => items?.map((item) => (item.id === link.id ? link : item)));
      return queryClient.invalidateQueries({ queryKey: SHARE_LINKS_KEY });
    },
  });
}

function postAction<T>(id: string, action: string, body: object): Promise<T> {
  return apiRequest<T>(`/api/admin/share-links/${id}/${action}`, { method: 'POST', body: JSON.stringify(body) });
}

export function useExtendShareLink(): UseMutationResult<{ link: ShareLink }, Error, { id: string; expiresOn: LocalDate }> {
  return useShareLinkMutation(({ id, expiresOn }) => postAction(id, 'extend', { expiresOn }));
}

export type VisibilityChange = { visibility: 'public'; confirm: true } | { visibility: 'sign_in_required' };

export function useSetShareLinkVisibility(): UseMutationResult<{ link: ShareLink }, Error, { id: string; change: VisibilityChange }> {
  return useShareLinkMutation(({ id, change }) => postAction(id, 'visibility', change));
}

export function useRevokeShareLink(): UseMutationResult<{ link: ShareLink }, Error, string> {
  return useShareLinkMutation((id: string) => postAction(id, 'revoke', {}));
}

export function useSharedReport(token: string): UseQueryResult<{ report: TeamReportDto }> {
  return useQuery({
    queryKey: ['shared-report', token],
    queryFn: () => apiRequest<{ report: TeamReportDto }>('/api/shared/report', { method: 'POST', body: JSON.stringify({ token }) }),
    meta: { silent: true },
    retry: (failureCount, error) => !(error instanceof ApiClientError && error.statusCode === HTTP_NOT_FOUND) && failureCount < MAX_SHARED_RETRIES,
  });
}
